import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { finApi, finErr } from "@/lib/financeApi";
import { btn, btnGold, btnGreen, btnRed, field } from "@/components/nds/windows/finance/parts";

export const useIsSuper = () => {
  const { user } = useAuth();
  return user?.role === "super_admin" || !!user?.is_super_admin;
};

/** Submit (staff, draft) · Approve (super, submitted) · Pay (super, approved) · Void (super, any non-voided). */
export default function InvoiceActions({ inv, onChanged, onDeleted }) {
  const isSuper = useIsSuper();
  const [mode, setMode] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const act = async (path, body, msg) => {
    setBusy(true);
    try { await finApi.patch(`/invoices/${inv.id}/${path}`, body); toast.success(msg); setMode(null); setVal(""); onChanged(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete draft invoice ${inv.invoice_number}? This can't be undone.`)) return;
    setBusy(true);
    try { await finApi.del(`/invoices/${inv.id}`); toast.success("Draft deleted"); (onDeleted || onChanged)(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  if (inv.status === "voided" || inv.legacy_import) return null;

  if (mode) {
    const pay = mode === "pay";
    return (
      <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <input autoFocus type={pay ? "number" : "text"} min="0.01" step="0.01" value={val} onChange={(e) => setVal(e.target.value)}
          placeholder={pay ? `Amount (balance ${inv.balance_due})` : "Void reason (required)"} data-testid={`fin-inv-${mode}-input-${inv.id}`}
          className={`${field} !py-1.5 ${pay ? "w-44" : "flex-1 min-w-[200px]"}`} />
        <button type="button" disabled={busy || (!pay && !val.trim())} data-testid={`fin-inv-${mode}-confirm-${inv.id}`} className={pay ? btnGreen : btnRed}
          onClick={() => (pay ? act("pay", val ? { paid_amount: Number(val) } : {}, "Payment posted to ledger") : act("void", { void_reason: val }, "Invoice voided"))}>
          Confirm {mode}
        </button>
        <button type="button" onClick={() => { setMode(null); setVal(""); }} data-testid={`fin-inv-${mode}-cancel-${inv.id}`} className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {inv.status === "draft" && <button type="button" disabled={busy} onClick={stop(() => act("submit", undefined, "Invoice submitted"))} data-testid={`fin-inv-submit-${inv.id}`} className={btnGold}>Submit</button>}
      {isSuper && inv.status === "submitted" && <button type="button" disabled={busy} onClick={stop(() => act("approve", undefined, "Invoice approved"))} data-testid={`fin-inv-approve-${inv.id}`} className={`${btn} border-sky-400/40 text-sky-300 hover:bg-sky-500/10`}>Approve</button>}
      {isSuper && inv.status === "approved" && <button type="button" onClick={stop(() => setMode("pay"))} data-testid={`fin-inv-pay-${inv.id}`} className={btnGreen}>Pay</button>}
      {inv.status === "draft" && <button type="button" disabled={busy} onClick={stop(del)} data-testid={`fin-inv-delete-${inv.id}`} className={btnRed}>Delete</button>}
      {isSuper && inv.status !== "draft" && <button type="button" onClick={stop(() => setMode("void"))} data-testid={`fin-inv-void-${inv.id}`} className={btnRed}>Void</button>}
    </div>
  );
}
